import { fabric } from 'fabric';
import { v4 as uuidv4 } from 'uuid';
import { CANVAS_JSON_PROPS, IFRAME_WIDGETS } from '../../../config';
import { disableTextResizeHandles } from '../../../utils/textControls';
import {
  getSlideVideos,
  handleVideo,
  MAX_VIDEOS_PER_SLIDE,
  VideoModeEnum,
} from './handleVideo';
import handleAddWebSite from './handleWebSite';
import handleWorldClockAndDate from './handleWorldClockAndDate';
import { applyQrControls } from './qrObject';

// A copied object, serialized like a saved slide object (with its data and
// locks) in slide coordinates. Copies get new ids.
export type ClipboardEntry = Record<string, any>;

export const PASTE_OFFSET = 10;

const COPY_PROPS = CANVAS_JSON_PROPS.filter((prop) => prop !== 'id');
const TEXT_TYPES = ['i-text', 'textbox', 'text'];

export const copyObjects = (
  canvas: fabric.Canvas,
  objects: fabric.Object[],
): ClipboardEntry[] =>
  objects.map((object) =>
    // The canvas serializer turns coordinates relative to a multi-selection
    // into slide coordinates; the JSON round trip detaches `data`, which the
    // serialized object shares with the live one.
    JSON.parse(
      JSON.stringify((canvas as any)._toObject(object, 'toObject', COPY_PROPS)),
    ),
  );

const enliven = (entry: ClipboardEntry) =>
  new Promise<fabric.Object | undefined>((resolve) =>
    fabric.util.enlivenObjects(
      [entry],
      ([object]: fabric.Object[]) => resolve(object),
      'fabric',
    ),
  );

const createObject = async (
  canvas: fabric.Canvas,
  entry: ClipboardEntry,
  recordHistory: () => void,
): Promise<fabric.Object | undefined> => {
  const type = entry.data?.type;
  // A video is a fabric image around a <video>; Fabric would try to load
  // the video file as an image.
  if (type === 'video') {
    return handleVideo({
      canvas,
      url: entry.data.url,
      fileType: entry.data.fileType,
      mode: VideoModeEnum.Thumbnail,
      left: entry.left,
      top: entry.top,
      // Serialized objects leave out `selectable`, and handleVideo copies
      // it over Fabric's default, which would make the copy unclickable.
      object: {
        ...entry,
        selectable: true,
        auto: true,
      } as unknown as fabric.Object,
      assetId: entry.data.assetId,
      thumbnailUrl: entry.data.thumbnailUrl,
    });
  }

  const isWidget = Object.values(IFRAME_WIDGETS).includes(type);
  const object = await enliven(
    // Each widget needs its own overlay, found by class name.
    isWidget
      ? { ...entry, data: { ...entry.data, className: uuidv4() } }
      : entry,
  );
  if (!object) return undefined;
  // @ts-ignore: added objects are recorded once, after the whole paste.
  object.auto = true;
  if (TEXT_TYPES.includes(object.type || '')) disableTextResizeHandles(object);
  applyQrControls(object);
  canvas.add(object);

  switch (type) {
    case IFRAME_WIDGETS.IFRAME:
      handleAddWebSite(canvas, object.data.src, recordHistory, object);
      break;
    case IFRAME_WIDGETS.WORLD_CLOCK:
    case IFRAME_WIDGETS.DATE:
      handleWorldClockAndDate({
        canvas,
        obj: object,
        isTime: type === IFRAME_WIDGETS.WORLD_CLOCK,
        handleHistory: recordHistory,
      });
      break;
  }
  return object;
};

// Adds copies of the entries, offset from the originals, selects them and
// records one undo step. Returns the entries moved by the same offset, so
// pasting again cascades, and how many videos were left out because the
// slide already has its video.
export const pasteObjects = async (
  canvas: fabric.Canvas,
  entries: ClipboardEntry[],
  recordHistory: () => void,
): Promise<{ shifted: ClipboardEntry[]; skippedVideos: number }> => {
  const shifted: ClipboardEntry[] = entries.map((entry) => ({
    ...entry,
    left: (entry.left || 0) + PASTE_OFFSET,
    top: (entry.top || 0) + PASTE_OFFSET,
  }));
  const created: fabric.Object[] = [];
  let videos = getSlideVideos(canvas).length;
  let skippedVideos = 0;
  for (const entry of shifted) {
    if (entry.data?.type === 'video') {
      if (videos >= MAX_VIDEOS_PER_SLIDE) {
        skippedVideos += 1;
        continue;
      }
      videos += 1;
    }
    const object = await createObject(canvas, entry, recordHistory);
    if (object) created.push(object);
  }
  if (!canvas.getContext() || !created.length) {
    return { shifted, skippedVideos };
  }
  canvas.discardActiveObject();
  canvas.setActiveObject(
    created.length === 1
      ? created[0]
      : new fabric.ActiveSelection(created, { canvas }),
  );
  canvas.requestRenderAll();
  recordHistory();
  return { shifted, skippedVideos };
};
